import { defineStore } from 'pinia';
import ProductService from '@/services/product';
import deepEqual from 'fast-deep-equal/es6';
import moment from 'moment';
import { useContentStore } from '@/stores/content';
import { useContentQueueStore } from '@/stores/contentQueue';
/*
    Hält Origins der Inhalte einzelner Bot Antworten im State.
*/

/** State of the content module (registered as `glimpseContent`) */
export interface ContentOriginsState {
  /** Published ("origin") content boxes: {[botId]: {[intent]: {[boxId]: box}}} */
  contentOrigins: Record<string, Record<string, Record<string, any>>>;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/content.ts` (registered as namespace
 * `glimpseContent` in `src/store/index.ts` — NOT to be confused with the sibling `content` module,
 * which lives at `src/store/modules/contentStore.ts` / `src/stores/content.ts`). In-memory only,
 * no `vuexPersist` option.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had BOTH a getter and an action named `getContent`. The getter keeps the name; the
 *   action is renamed `getContentAction`.
 * - Mutations `resetOrigins`, `setContentOrigin`, `setContentOrigins` had no same-named actions —
 *   they become same-named actions directly.
 * - Legacy action `getContent`'s internal `commit('setContentOrigins', cache)` (no `{root: true}`)
 *   is a local commit — becomes `this.setContentOrigins(cache)`.
 *
 * Cross-store references (see STORES.md):
 * - `rootGetters['content/getContentBoxes']` → `useContentStore().getContentBoxes(...)`.
 * - `commit('content/setContentDrafts', ..., { root: true })` → `useContentStore().setContentDrafts(...)`.
 * - `rootGetters['contentQueue/*']` / `commit('contentQueue/*', ..., { root: true })` →
 *   `useContentQueueStore()` getters/actions of the same name.
 */
export const useGlimpseContentStore = defineStore('glimpseContent', {
  state: (): ContentOriginsState => ({
    contentOrigins: {}, // store for origin versions
  }),
  getters: {
    getContentBoxes(): (botId: string, intent: string, channel?: string | false) => any[] {
      return (botId: string, intent: string, channel?: string | false) => {
        if (!this.contentOrigins[botId] || !this.contentOrigins[botId][intent]) {
          return [];
        }

        let allBoxes = Object.keys(this.contentOrigins[botId][intent])
          .map((box) => {
            return this.contentOrigins[botId][intent][box];
          })
          .filter((box) => {
            if (channel === false) {
              return true;
            } else if (!channel && box.channel) {
              return false;
            } else if (!channel && !box.channel) {
              return true;
            } else if (channel && box.channel && channel === box.channel) {
              return true;
            }

            return false;
          });

        return allBoxes;
      };
    },
    /**
     * Renamed usage note: this GETTER keeps the legacy name `getContent` — the legacy ACTION of
     * the same name is renamed `getContentAction` below (getter wins the name per collision rule).
     */
    getContent(): (botId: string, intent: string, box: string) => any {
      return (botId: string, intent: string, box: string) => {
        // get origins
        if (
          !this.contentOrigins[botId] ||
          !this.contentOrigins[botId][intent] ||
          !this.contentOrigins[botId][intent][box]
        ) {
          return null;
        }
        return this.contentOrigins[botId][intent][box];
      };
    },
    isContentChanged(): (botId: string, intent: string, channel?: string | false) => boolean {
      return (botId: string, intent: string, channel?: string | false) => {
        const contentStore = useContentStore();
        if (!contentStore) return false;

        let originBoxes = this.getContentBoxes(botId, intent, channel);
        if (originBoxes.length === 0) return false;

        // get version number from intent and channel
        // is version number same global version number dann return cache

        //

        let allBoxes = contentStore.getContentBoxes(botId, intent, channel);
        let notEqual = false;
        for (let box of allBoxes) {
          if (
            !this.contentOrigins[botId] ||
            !this.contentOrigins[botId][intent] ||
            !this.contentOrigins[botId][intent][box.box]
          ) {
            notEqual = true;
            break;
          }
          if (!deepEqual(this.contentOrigins[botId][intent][box.box], box)) {
            notEqual = true;
            break;
          }
        }
        return notEqual;
      };
    },
    isUpdatedContentAvailable(): (botId: string, intent: string, channel?: string | false) => boolean {
      return (botId: string, intent: string, channel?: string | false) => {
        const contentStore = useContentStore();
        let draftBoxes = contentStore.getContentBoxes(botId, intent, channel);
        // `false` acts as a "not set yet" sentinel, otherwise holds a date string
        let modificationDate: any = false;
        draftBoxes.forEach((b: any) => {
          if (modificationDate === false && b.modificationDate) {
            modificationDate = b.modificationDate;
          }

          if (
            moment(new Date(modificationDate)).isBefore(
              moment(new Date(b.modificationDate))
            )
          ) {
            modificationDate = b.modificationDate;
          }
        });

        let originModificationDate: any = false;

        if (!this.contentOrigins[botId] || !this.contentOrigins[botId][intent]) {
          return false;
        }

        for (let box of draftBoxes) {
          if (
            this.contentOrigins[botId][intent][box.box] &&
            this.contentOrigins[botId][intent][box.box].modificationDate
          ) {
            if (
              originModificationDate === false ||
              moment(new Date(originModificationDate)).isBefore(
                moment(
                  new Date(
                    this.contentOrigins[botId][intent][box.box].modificationDate
                  )
                )
              )
            ) {
              originModificationDate =
                this.contentOrigins[botId][intent][box.box].modificationDate;
            }
          }
        }

        if (modificationDate === false || originModificationDate === false) {
          return false;
        }

        return moment(new Date(modificationDate)).isBefore(
          moment(new Date(originModificationDate))
        );
      };
    },
  },
  actions: {
    /**
     * Renamed from legacy action `getContent` (collides with the getter of the same name — the
     * getter keeps the name).
     *
     * it fetches the content from api and it saves it to the origin content store.
     *
     * if preloading is set, all follow up boxes will be queued and processed.
     */
    async getContentAction({ step, botId, intent, template, box, channel, preloading }: any) {
      // returns queue promise
      return new Promise(async (done) => {
        const contentQueueStore = useContentQueueStore();
        let cache: any[] = []; // caches results and commit when done
        let parentBox: any = false;

        let finishFetching = async () => {
          await contentQueueStore.removeWorker();
          setTimeout(nextTask, 100);
        };

        let fetching = async (params: any, doneFetching: () => void) => {
          if (!params.template || !params.intent || !params.botId) {
            return doneFetching();
          }

          let originContent = await ProductService.getContent(
            params.botId,
            params.template,
            params.intent,
            params.box,
            params.channel
          );
          if (
            originContent.result &&
            originContent.result.template &&
            originContent.result.box !== false
          ) {
            if (params.step !== undefined && params.step !== null) {
              originContent.result.step = params.step;
            }

            // Wenn kein Origin Content im State liegt, oder das Template identisch ist und es nicht nur flüchtig ist (onlyDraft).
            if (!originContent.result.onlyDraft) {
              cache.push({
                content: originContent.result,
                botId: params.botId,
                intent: params.intent,
                box: originContent.result.box,
              });

              // Pre loading aller Inhalte
              if (
                params.preloading &&
                originContent.result.followup &&
                originContent.result.followup.box
              ) {
                await contentQueueStore.addQueue({
                  element: {
                    step: params.step + 1,
                    botId: params.botId,
                    template: originContent.result.followup.template,
                    intent: params.intent,
                    box: originContent.result.followup.box,
                    channel: params.channel,
                    preloading: params.preloading,
                  },
                });
              } else if (
                params.preloading &&
                Array.isArray(originContent.result.answers)
              ) {
                for (let answer of originContent.result.answers) {
                  if (answer.box) {
                    await contentQueueStore.addQueue({
                      element: {
                        step: params.step + 1,
                        botId: params.botId,
                        template: answer.template,
                        intent: params.intent,
                        box: answer.box,
                        channel: params.channel,
                        preloading: params.preloading,
                      },
                    });
                  }
                }
              } else if (
                params.preloading &&
                Array.isArray(originContent.result.childBoxes)
              ) {
                for (let childbox of originContent.result.childBoxes) {
                  await contentQueueStore.addQueue({
                    element: {
                      botId: params.botId,
                      template: childbox.template,
                      intent: params.intent,
                      box: childbox.box,
                      channel: params.channel,
                      preloading: params.preloading,
                    },
                  });
                }
              } else if (
                params.box === undefined &&
                originContent &&
                originContent.result &&
                originContent.result.box
              ) {
                params.box = originContent.result.box;
              }
            }

            if (
              (originContent.result.onlyDraft && !params.box) ||
              params.channel
            ) {
              params.box = originContent.result.box;
            }
            // set box of first/parent request
            if (parentBox === false) {
              parentBox = params.box || originContent.result.box;
            }

            return doneFetching();
          } else {
            return doneFetching();
          }
        };

        let nextTask = async () => {
          if (contentQueueStore.isSyncing) {
            if (contentQueueStore.workerSlotsAvailable) {
              // work to do
              let queues = contentQueueStore.getQueues;
              await contentQueueStore.addWorker();
              let element = contentQueueStore.getQueue(queues[0]);
              await contentQueueStore.removeQueue(queues[0]);
              fetching(element, finishFetching);
              setTimeout(nextTask, 100);
              /*  nextTask(); */
            } else if (contentQueueStore.isEmpty) {
              // finish queue and commit cache
              await contentQueueStore.finish();
              await this.setContentOrigins(cache);
              done(parentBox);
            }
          }
          // nothing todo
        };

        // add element to queue
        await contentQueueStore.addQueue({
          element: { step, botId, template, intent, box, channel, preloading },
        });

        if (!contentQueueStore.isSyncing) {
          await contentQueueStore.start();
          setTimeout(nextTask, 10);
        }
      });
    },
    /**
     * it is called when switching to the edit mode.
     * it handles the origin -> draft copy, if no draft available
     *
     * TODO draft outdated feature
     */
    async copyOrigin({ botId, name, channel }: { botId: string; name: string; channel?: string | false }) {
      const contentStore = useContentStore();
      let draftBoxes = contentStore.getContentBoxes(botId, name, channel);

      if (draftBoxes.length > 0) {
        return;
      }
      /* if (state.contentDrafts && state.contentDrafts[botId] && state.contentDrafts[botId][name]) {
              // all draft boxes from channel
              let allBoxes = Object.keys(state.contentDrafts[botId][name]).map(box => {
                  return state.contentDrafts[botId][name][box];
              }).filter(box => {
                  if (!channel && box.channel) {
                      return false;
                  } else if (!channel && !box.channel) {
                      return true && (box.template === 'SimpleInformation');
                  } else if (channel && box.channel && channel === box.channel) {
                      return true && (box.template === 'SimpleInformation');
                  }

                  return false;
              })

              if (allBoxes.length > 0) {
                  return;
              }
          } */
      if (
        this.contentOrigins &&
        this.contentOrigins[botId] &&
        this.contentOrigins[botId][name]
      ) {
        let allBoxes = Object.keys(this.contentOrigins[botId][name])
          .map((box: string) => {
            return this.contentOrigins[botId][name][box];
          })
          .filter((box: any) => {
            /*   if (state.contentDrafts && state.contentDrafts[botId] && state.contentDrafts[botId][name] && state.contentDrafts[botId][name][box.box]) {
                      return false;
                  } */

            if (!channel && box.channel) {
              return false;
            } else if (!channel && !box.channel) {
              return true;
            } else if (channel && box.channel && channel === box.channel) {
              return true;
            }

            return false;
          });
        contentStore.setContentDrafts({ drafts: allBoxes, botId });
      }
    },
    /**
     * Converted from legacy mutation `resetOrigins` (no matching legacy action existed).
     */
    resetOrigins({ botId, name, channel }: { botId: string; name: string; channel?: string | false }) {
      // if their is no local changes remove unchanged draft too.
      if (channel === undefined) {
        channel = false;
      }

      if (
        this.contentOrigins &&
        this.contentOrigins[botId] &&
        this.contentOrigins[botId][name]
      ) {
        // all draft boxes from channel
        let allBoxes = Object.keys(this.contentOrigins[botId][name])
          .map((box) => {
            return this.contentOrigins[botId][name][box];
          })
          .filter((box) => {
            if (channel === false) {
              return true;
            } else if (!channel && box.channel) {
              return false;
            } else if (!channel && !box.channel) {
              return true;
            } else if (channel && box.channel && channel === box.channel) {
              return true;
            }

            return false;
          });

        if (allBoxes.length > 0) {
          // delete all drafts
          for (let box of allBoxes) {
            delete this.contentOrigins[botId][name][box.box];
          }
        }
      }
    },
    /**
     * Converted from legacy mutation `setContentOrigin` (no matching legacy action existed).
     */
    setContentOrigin({ content, botId, intent, box }: { content: any; botId: string; intent: string; box: string }) {
      const clone = JSON.parse(JSON.stringify(content));
      if (!this.contentOrigins[botId]) {
        this.contentOrigins = { ...this.contentOrigins, [botId]: {} };
      }

      if (!this.contentOrigins[botId][intent]) {
        this.contentOrigins[botId] = {
          ...this.contentOrigins[botId],
          [intent]: {},
        };
      }

      this.contentOrigins[botId][intent] = {
        ...this.contentOrigins[botId][intent],
        [box]: clone,
      };
    },
    /**
     * Converted from legacy mutation `setContentOrigins` (no matching legacy action existed).
     * Called locally from `getContentAction` (legacy `commit('setContentOrigins', cache)` with no
     * `{ root: true }`).
     */
    setContentOrigins(list: Array<{ content: any; botId: string; intent: string; box: string }>) {
      for (let l of list) {
        if (!l.content.createChannel) {
          const clone = JSON.parse(JSON.stringify(l.content));

          if (!this.contentOrigins[l.botId]) {
            this.contentOrigins = { ...this.contentOrigins, [l.botId]: {} };
          }

          if (!this.contentOrigins[l.botId][l.intent]) {
            this.contentOrigins[l.botId] = {
              ...this.contentOrigins[l.botId],
              [l.intent]: {},
            };
          }

          this.contentOrigins[l.botId][l.intent] = {
            ...this.contentOrigins[l.botId][l.intent],
            [l.box]: clone,
          };
        }
      }
    },
  },
});
